import { useState, useEffect } from "react";
import styled from "styled-components";
import { Capacitor } from "@capacitor/core";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { White, Purple, Blue } from "../theme/colors";
import { P3 } from "../theme/text/Headings";

const BannerContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, ${Purple} 0%, ${Blue} 100%);
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(136, 0, 255, 0.25);
  backdrop-filter: blur(10px);
  animation: slideDown 0.3s ease-out;

  @keyframes slideDown {
    from {
      transform: translateY(-100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const BannerContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  backdrop-filter: blur(10px);
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
`;

const BannerTitle = styled(P3)`
  margin: 0;
  font-weight: 700;
  font-size: 14px;
  line-height: 1.2;
`;

const BannerSubtitle = styled(P3)`
  margin: 0;
  font-size: 11px;
  opacity: 0.9;
  line-height: 1.2;
`;

const OpenButton = styled.button`
  background: ${White};
  color: ${Purple};
  border: none;
  border-radius: 24px;
  padding: 10px 20px;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: none;
  color: ${White};
  cursor: pointer;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  margin-left: 8px;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }

  &:active {
    opacity: 0.7;
    transform: scale(0.95);
  }
`;

const OpenInAppBanner = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Only show on mobile browser (not in native app)
    const isMobileBrowser =
      !Capacitor.isNativePlatform() &&
      /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    // Check if user has previously dismissed the banner
    const hasDismissed = sessionStorage.getItem("openInAppBannerDismissed");

    if (isMobileBrowser && !hasDismissed) {
      setIsVisible(true);
    }
  }, []);

  const handleOpenInApp = () => {
    // Try to open in app using custom URL scheme
    let currentPath = window.location.pathname + window.location.search;

    // Handle /s/ and /p/ prefixed routes (server-side meta routes)
    // Strip these so the app opens to the correct page
    if (currentPath.startsWith("/s/")) {
      // /s/personality-check/mehak?from=123 → /personality-check?from=123
      const parts = currentPath.split("?");
      const pathPart = parts[0];
      const query = parts[1] ? `?${parts[1]}` : "";
      const segments = pathPart.split("/").filter(Boolean);

      if (segments.length >= 2) {
        currentPath = `/${segments[1]}${query}`;
      }
    } else if (currentPath.startsWith("/p/")) {
      // /p/username → /username
      currentPath = currentPath.replace("/p/", "/");
    }

    // Remove leading slash for custom URL scheme
    const pathWithoutLeadingSlash = currentPath.startsWith("/")
      ? currentPath.substring(1)
      : currentPath;

    const appUrl = `vibe-check://${pathWithoutLeadingSlash}`;

    // Attempt to open the app with the custom URL scheme
    // If the app is installed, it will open immediately and this page will go to background
    window.location.href = appUrl;

    // Fallback: If the app doesn't open after 2.5 seconds (meaning it's not installed),
    // redirect to the App Store so the user can download it
    setTimeout(() => {
      // Only redirect if still on this page (app didn't open)
      if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        window.location.href =
          "https://apps.apple.com/app/vibe-check-compatibility-quiz/id6755138167";
      }
      // Note: For Android, you would add Google Play Store link here
    }, 2500);
  };

  const handleDismiss = () => {
    sessionStorage.setItem("openInAppBannerDismissed", "true");
    setIsVisible(false);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <BannerContainer>
      <BannerContent>
        <IconWrapper>
          <OpenInNewIcon style={{ fontSize: 18, color: White }} />
        </IconWrapper>
        <TextContent>
          <BannerTitle color={White}>Open in Vibe Check App</BannerTitle>
          <BannerSubtitle color={White}>Get the full experience</BannerSubtitle>
        </TextContent>
        <OpenButton onClick={handleOpenInApp}>Open</OpenButton>
      </BannerContent>
      <CloseButton onClick={handleDismiss} aria-label="Dismiss banner">
        <CloseIcon style={{ fontSize: 18 }} />
      </CloseButton>
    </BannerContainer>
  );
};

export default OpenInAppBanner;
